import { getCanaryIdentity } from "./canaryConfig";

// Keep this application-level list aligned with the non-customer values in the database enum.
// The database retains customer as a legacy value; customer is always classification NULL in application rules.
export const ENTITY_CLASSIFICATIONS = [
  "internal",
  "test",
  "demo",
  "canary",
  "ci",
] as const;
export type EntityClassification = (typeof ENTITY_CLASSIFICATIONS)[number];

// Machine-generated traffic, excluded from customer-facing views by default.
export const SYNTHETIC_CLASSIFICATIONS = ["canary", "test", "ci"] as const;

const REPLO_EMAIL_DOMAIN = "replo.app";

// Automation accounts on untagged replo.app addresses, which would otherwise classify internal and reach customer triage.
const KNOWN_TEST_EMAILS: ReadonlySet<string> = new Set([
  "sidd-eval-runner@replo.app",
]);

/** Email-derived classification at signup, most-specific rule first (canary emails contain `+test` and end in `@replo.app` but must classify canary); null = customer-by-default. Workspaces inherit their creator, projects their workspace. */
export function getUserClassification({
  email,
}: {
  email: string;
}): EntityClassification | null {
  const normalized = email.toLowerCase();
  if (getCanaryIdentity({ email: normalized }).isCanary) {
    return "canary";
  }
  if (KNOWN_TEST_EMAILS.has(normalized)) {
    return "test";
  }
  const [localPart = "", domain] = normalized.split("@");
  const tags = new Set(localPart.split("+").slice(1));
  if (tags.has("canary")) {
    return "canary";
  }
  if (tags.has("ci")) {
    return "ci";
  }
  if (tags.has("test")) {
    return "test";
  }
  if (domain === REPLO_EMAIL_DOMAIN) {
    return "internal";
  }
  return null;
}

/** Canary, test and CI accounts; analytics drops their events. */
export function isSyntheticEmail(email: string): boolean {
  const classification = getUserClassification({ email });
  return SYNTHETIC_CLASSIFICATIONS.some((value) => value === classification);
}
